import { Injectable } from '@angular/core';
import { CaseAnalysisService } from '../case-analysis/case-analysis.service';
import { AuthService } from '@telespot/web-core';
import { Observable } from 'rxjs';

import { IAnalyst, IFavAssetInfo, ILabel, IReportFilter, ISampleAnalysis, ISampleCountEntity } from '../../state';
import { Store } from '@ngrx/store';
import * as ReportsSelectors from '../../state/reports.selectors';
import * as ReportsActions from '../../state/reports.actions';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

@Injectable({
  providedIn: 'root',
})
export class ReportGeneratorService {
  /** PRIVATE VARIABLES */
  private readonly PADDING_VALUE = 16;
  private readonly SCALE = 2;
  private readonly options = { scale: this.SCALE, useCORS: true };

  /** PUBLIC VARIABLES */
  public readonly samples$ = this.store$.select(ReportsSelectors.getAllSamples);
  public readonly visibleSamples$ = this.store$.select(ReportsSelectors.visibleSamples);
  public readonly methodTypes$ = this.store$.select(ReportsSelectors.getAllMethodTypes);
  public readonly visibleMethodTypes$ = this.store$.select(ReportsSelectors.visibleMethodTypes);
  public readonly activeMethodType$ = this.store$.select(ReportsSelectors.activeMethodType);
  public readonly activeSample$ = this.store$.select(ReportsSelectors.getActiveSample);
  public readonly activeFilters$ = this.store$.select(ReportsSelectors.activeFilters);
  public readonly analysts$ = this.store$.select(ReportsSelectors.selectAnalysts);
  public readonly labelOptions$ = this.store$.select(ReportsSelectors.activeLabelOptions);
  public readonly conclusions$ = this.store$.select(ReportsSelectors.caseConclusions);
  public readonly samplesInfo$ = this.store$.select(ReportsSelectors.getSamplesInfo);
  public readonly loading$ = this.store$.select(ReportsSelectors.loading);
  public readonly loadingCrops$ = this.store$.select(ReportsSelectors.loadingCrops);
  public readonly cropAnalyst$ = this.store$.select(ReportsSelectors.cropAnalyst);
  public readonly case$ = this.caseService.case$;

  constructor(private caseService: CaseAnalysisService, private authService: AuthService, private store$: Store) {}

  public getSampleAnalysesBySampleId(sampleId: string): Observable<ISampleAnalysis[]> {
    return this.store$.select(ReportsSelectors.getSampleAnalysesBySampleId(sampleId));
  }

  public getSampleCountersBySampleId(sampleId: string): Observable<ISampleCountEntity[]> {
    return this.store$.select(ReportsSelectors.getSampleCountersBySampleId(sampleId));
  }

  /** Analysts holding an analysis state on this sample — empty means nobody analysed it. */
  public getAnalystsForSample(sampleId: string): Observable<IAnalyst[]> {
    return this.store$.select(ReportsSelectors.getAnalystsForSample(sampleId));
  }

  public getLabelsByMethodTypeId(methodTypeId: string): Observable<ILabel[]> {
    return this.store$.select(ReportsSelectors.getLabelsByMethodTypeId(methodTypeId));
  }

  public getFavAssetsFromSample(sampleId: string): Observable<IFavAssetInfo[]> {
    return this.store$.select(ReportsSelectors.getFavAssetsFromSample(sampleId));
  }

  public setActiveMethodType(id: string) {
    this.store$.dispatch(ReportsActions.setActiveMethodType({ methodTypeId: id }));
  }

  public setActiveSample(id: string) {
    this.store$.dispatch(ReportsActions.setActiveSample({ sampleId: id }));
  }

  public getCropInfoBySampleId(id: string) {
    return this.store$.select(ReportsSelectors.getCropsBySampleId(id));
  }

  public updateFilter(filter: IReportFilter) {
    this.store$.dispatch(ReportsActions.setupFilter({ filter }));
  }

  public loadCrops(analyst: IAnalyst) {
    this.store$.dispatch(ReportsActions.cropAnalystChanged({ analyst }));
  }

  public onConclusionsChange(conclusions: string): void {
    this.store$.dispatch(ReportsActions.updateConclusions({ conclusions }));
  }

  public initializeReport(caseId: string) {
    this.store$.dispatch(ReportsActions.initializeReport({ caseId }));
  }

  public clearReportState() {
    this.store$.dispatch(ReportsActions.clearReportsState());
  }

  public getNumSamples(methodTypeId: string) {
    return this.store$.select(ReportsSelectors.numSamplesByMethodType(methodTypeId));
  }

  public getVisibleSamplesForMethodType(methodTypeId: string) {
    return this.store$.select(ReportsSelectors.getVisibleSamplesForMethodType(methodTypeId));
  }

  public getNumVisibleSamplesByMethodType(methodTypeId: string) {
    return this.store$.select(ReportsSelectors.numVisibleSamplesByMethodType(methodTypeId));
  }

  public loadSamplesInfo(sampleIds) {
    return this.store$.dispatch(ReportsActions.loadInfoForSamples({ sampleIds }));
  }

  public getSamplesFromMethodType(id: string) {
    return this.store$.select(ReportsSelectors.getSamplesForMethodType(id));
  }

  /**
   * Exports the report as a PDF file with intelligent page breaks
   *
   * This method generates a PDF from the HTML report container using html2canvas and jsPDF.
   * It includes sophisticated page break logic to ensure content is properly distributed
   * across pages without cutting important elements like tables or images.
   *
   * @param element - The HTML element containing the report to export
   * @param caseName - Name of the case for the PDF filename
   * @param workspaceName - Name of the workspace for the PDF header
   * @param htmlElementsItems - Pre-analyzed DOM elements with position information
   * @returns Promise that resolves when PDF generation is complete
   */
  public async exportReportAsPdf(
    element: HTMLElement,
    caseName: string,
    workspaceName: string,
    htmlElementsItems: any[]
  ): Promise<void> {
    try {
      const canvas = await html2canvas(element, this.options);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      await this.addContentToPdf(pdf, canvas, workspaceName, htmlElementsItems);

      const exportDate = new Date().toLocaleDateString();
      pdf.save(`${caseName}_${exportDate}_report.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  }

  /**
   * Adds content to the PDF with intelligent page breaks and proper formatting
   *
   * The method uses sophisticated algorithms to determine optimal page breaks,
   * ensuring that important content like tables and images are not cut in half.
   *
   * @param pdf - The jsPDF instance to add content to
   * @param canvas - The HTML canvas containing the rendered report
   * @param workspaceName - Name of the workspace for the header
   * @param htmlElementsItems - Pre-analyzed DOM elements with position information
   */
  private async addContentToPdf(
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    workspaceName: string,
    htmlElementsItems: any[]
  ): Promise<void> {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const scaleFactor = (pageWidth - margin * 2) / canvasWidth;

    let positionY = 0; // Tracks the current vertical position of the content
    let currentPage = 1;

    const exportDate = new Date().toLocaleDateString();

    const organizationLogoUrl = this.authService.currentOrganizationValue.logoUrl;
    const spotlabLogoUrl = 'assets/media/logo/spotlab_logo.png';

    // Add header and get its height
    const headerHeight = await this.addHeader(pdf, organizationLogoUrl, spotlabLogoUrl, margin, pageWidth);

    // Adjusted available height per page (accounting for header and footer)
    const footerHeight = 15;
    const availablePageHeight = pageHeight - margin * 2 - headerHeight - margin - footerHeight;

    while (positionY < canvasHeight) {
      const remainingHeight = canvasHeight - positionY;

      // Height to render on this page, adjusted for logo
      let renderHeight = Math.min(remainingHeight, availablePageHeight / scaleFactor);

      if (renderHeight !== remainingHeight) {
        renderHeight =
          this.getRenderHeightBasedOnContent(htmlElementsItems, positionY, renderHeight) +
          this.PADDING_VALUE * this.SCALE;
      }

      // Create a temporary canvas to hold the current portion
      const croppedCanvas = document.createElement('canvas');
      const croppedContext = croppedCanvas.getContext('2d');

      croppedCanvas.width = canvasWidth;
      croppedCanvas.height = renderHeight;

      // Crop the canvas for the current page
      croppedContext.drawImage(canvas, 0, positionY, canvasWidth, renderHeight, 0, 0, canvasWidth, renderHeight);

      const croppedImage = croppedCanvas.toDataURL('image/png');

      // Add the cropped content below the header
      pdf.addImage(
        croppedImage,
        'PNG',
        margin,
        headerHeight + margin * 2,
        pageWidth - margin * 2,
        renderHeight * scaleFactor
      );

      // Add footer with page number and export information
      this.addFooter(pdf, currentPage, exportDate, workspaceName, pageWidth, pageHeight, margin);

      positionY += renderHeight;

      // Add a header and footer to the new page
      if (positionY < canvasHeight) {
        pdf.addPage();
        currentPage++;

        await this.addHeader(pdf, organizationLogoUrl, spotlabLogoUrl, margin, pageWidth);
        this.addFooter(pdf, currentPage, exportDate, workspaceName, pageWidth, pageHeight, margin);
      }
    }
  }

  private async addHeader(
    pdf: jsPDF,
    leftLogoUrl: string,
    rightLogoUrl: string,
    margin: number,
    pageWidth: number
  ): Promise<number> {
    let maxLogoHeight = 15;

    // Add left logo (organization logo)
    if (leftLogoUrl) {
      try {
        const leftLogoHeight = await this.addLogo(pdf, leftLogoUrl, margin, 'left');
        maxLogoHeight = Math.max(maxLogoHeight, leftLogoHeight);
      } catch (error) {
        console.warn('Failed to load left logo dimensions, using defaults:', error);
      }
    }

    // Add right logo (spotlab logo)
    if (rightLogoUrl) {
      try {
        const rightLogoHeight = await this.addLogo(pdf, rightLogoUrl, margin, 'right', pageWidth);
        maxLogoHeight = Math.max(maxLogoHeight, rightLogoHeight);
      } catch (error) {
        console.warn('Failed to load right logo dimensions, using defaults:', error);
      }
    }

    return maxLogoHeight;
  }

  private async addLogo(
    pdf: jsPDF,
    logoUrl: string,
    margin: number,
    align: 'left' | 'right',
    pageWidth?: number
  ): Promise<number> {
    const imageDimensions = await this.getImageDimensions(logoUrl);
    const calculatedDimensions = this.calculateLogoDimensions(imageDimensions.width, imageDimensions.height);
    const logoWidth = calculatedDimensions.width;
    const logoHeight = calculatedDimensions.height;

    const logoX = align === 'left' ? margin : pageWidth - margin - logoWidth;
    const logoY = margin;

    pdf.addImage(logoUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);

    return logoHeight;
  }

  /**
   * Extracts the natural dimensions of an image from its URL
   *
   * This method loads an image and returns its natural width and height,
   * which can be used to maintain proper aspect ratios for logos of different
   * shapes (square, landscape, portrait).
   *
   * @param imageUrl - URL of the image to analyze
   * @returns Promise that resolves to an object with width and height properties
   */
  private async getImageDimensions(imageUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };

      img.onerror = () => {
        // Fallback to default dimensions if image fails to load
        resolve({ width: 30, height: 20 });
      };

      img.src = imageUrl;
    });
  }

  /**
   * Calculates optimal logo dimensions maintaining aspect ratio
   *
   * This method takes the natural image dimensions and calculates
   * appropriate display dimensions that fit within the PDF header
   * while maintaining the original aspect ratio.
   *
   * @param naturalWidth - Original width of the image
   * @param naturalHeight - Original height of the image
   * @param maxWidth - Maximum allowed width in mm
   * @param maxHeight - Maximum allowed height in mm
   * @returns Object with calculated width and height in mm
   */
  private calculateLogoDimensions(
    naturalWidth: number,
    naturalHeight: number,
    maxWidth = 30,
    maxHeight = 20
  ): { width: number; height: number } {
    const aspectRatio = naturalWidth / naturalHeight;

    let width = maxWidth;
    let height = maxWidth / aspectRatio;

    // If height exceeds maximum, scale down based on height
    if (height > maxHeight) {
      height = maxHeight;
      width = maxHeight * aspectRatio;
    }

    return { width, height };
  }

  /**
   * Adds footer to the PDF page with page number and export information
   *
   * This method adds a footer to each page containing:
   * - Page number on the left
   * - Export date and workspace information on the right
   *
   * @param pdf - The jsPDF instance to add footer to
   * @param pageNumber - Current page number
   * @param exportDate - Date when the report was exported
   * @param workspaceName - Name of the workspace
   * @param pageWidth - Width of the page in mm
   * @param pageHeight - Height of the page in mm
   * @param margin - Margin size in mm
   */
  private addFooter(
    pdf: jsPDF,
    pageNumber: number,
    exportDate: string,
    workspaceName: string,
    pageWidth: number,
    pageHeight: number,
    margin: number
  ): void {
    const footerY = pageHeight - margin;
    const textHeight = 8;

    pdf.setFontSize(textHeight);

    // Add page number on the left
    pdf.text(`${pageNumber}`, margin, footerY, {
      align: 'left',
    });

    // Add export information on the right
    pdf.text(`Exported: ${exportDate}`, pageWidth - margin, footerY, {
      align: 'right',
    });
    pdf.text(`Workspace: ${workspaceName}`, pageWidth - margin, footerY - 4, {
      align: 'right',
    });
  }

  /**
   * Finds the last visible child element within the current page range
   *
   * This method is crucial for determining where to break pages. It recursively
   * searches through child elements to find the last one that fits within the
   * current page boundaries, ensuring content isn't cut inappropriately.
   *
   * @param children - Array of child elements to analyze
   * @param referenceY - Current vertical position in the canvas
   * @param renderHeight - Maximum height available for this page
   * @returns The last visible child element or null if none found
   */
  private getLastVisibleChild(children: any[], referenceY: number, renderHeight: number): any {
    const visibleChildren = children.filter((child) => {
      return child.yPosition + child.height >= referenceY && child.yPosition < referenceY + renderHeight;
    });

    if (visibleChildren.length === 0) {
      return null;
    }

    let index = visibleChildren.length - 1;

    while (index >= 0) {
      const currentChild = visibleChildren[index];

      if (currentChild.children && currentChild.children.length > 0) {
        const nestedLastVisibleChild = this.getLastVisibleChild(currentChild.children, referenceY, renderHeight);

        if (
          nestedLastVisibleChild &&
          nestedLastVisibleChild.yPosition + nestedLastVisibleChild.height <= referenceY + renderHeight
        ) {
          return nestedLastVisibleChild;
        }
      }

      const isTitle = currentChild.element.tagName.startsWith('H');
      if (
        currentChild.yPosition + currentChild.height <=
        referenceY + (!isTitle ? renderHeight : renderHeight + this.PADDING_VALUE * 3 * this.SCALE)
      ) {
        return currentChild;
      }

      index--; // Move to the previous child
    }

    return null;
  }

  /**
   * Calculates the optimal render height for the current page, considering page break rules
   * @param items - Array of DOM elements with position information
   * @param referenceY - Current vertical position in the canvas
   * @param renderHeight - Maximum height available for this page
   * @returns Optimal height to render on this page
   */
  private getRenderHeightBasedOnContent(items: any[], referenceY: number, renderHeight: number): number {
    const visibleItems = items.filter((item) => {
      return item.yPosition + item.height >= referenceY && item.yPosition < referenceY + renderHeight;
    });

    if (visibleItems.length === 0) {
      return renderHeight;
    }

    // Check for analysis-result-title - force page break BEFORE it
    const analysisTitle = this.findElementById(items, 'analysis-result-title');
    if (analysisTitle && analysisTitle.yPosition >= referenceY && analysisTitle.yPosition < referenceY + renderHeight) {
      return analysisTitle.yPosition - referenceY;
    }

    // Check if any page break element falls within the current page range
    for (const element of this.findAllPageBreakElements(items)) {
      if (element.yPosition >= referenceY && element.yPosition < referenceY + renderHeight) {
        return element.yPosition - referenceY;
      }
    }

    // Fallback to original logic for content that doesn't need page breaks
    return this.calculateFallbackRenderHeight(visibleItems, referenceY, renderHeight);
  }

  /**
   * Fallback method for calculating render height when no special page breaks are needed
   */
  private calculateFallbackRenderHeight(visibleItems: any[], referenceY: number, renderHeight: number): number {
    let lastVisibleItem = visibleItems[visibleItems.length - 1];

    if (lastVisibleItem.children && lastVisibleItem.children.length > 0) {
      const lastVisibleChild = this.getLastVisibleChild(lastVisibleItem.children, referenceY, renderHeight);

      if (lastVisibleChild) {
        const newRenderHeight = lastVisibleChild.yPosition + lastVisibleChild.height - referenceY;
        return this.getRenderHeightBasedOnContent([lastVisibleChild], referenceY, newRenderHeight);
      }
    }

    lastVisibleItem = visibleItems[visibleItems.length - 2];
    if (lastVisibleItem) {
      return lastVisibleItem.yPosition + lastVisibleItem.height - referenceY;
    }

    return renderHeight;
  }

  /**
   * Finds all elements that should trigger page breaks
   * @param items - Array of DOM elements with position information
   * @returns Array of elements sorted by vertical position
   */
  private findAllPageBreakElements(items: any[]): any[] {
    const pageBreakElements: any[] = [];

    // Find conclusions-title - force page break BEFORE it
    const conclusionsTitle = this.findElementById(items, 'conclusions-title');
    if (conclusionsTitle) {
      pageBreakElements.push(conclusionsTitle);
    }

    // Find all sample titles - force page break BEFORE them (except the first sample)
    const sampleTitles = this.findAllSampleTitles(items);
    if (sampleTitles.length > 0) {
      // Skip the first sample, add all others
      for (let i = 1; i < sampleTitles.length; i++) {
        pageBreakElements.push(sampleTitles[i]);
      }
    }

    // Sort by vertical position to ensure correct page break order
    return pageBreakElements.sort((a, b) => a.yPosition - b.yPosition);
  }

  /**
   * Recursively searches for an element by its ID in the DOM structure
   * @param items - Array of DOM elements to search through
   * @param id - ID of the element to find
   * @returns The element if found, null otherwise
   */
  private findElementById(items: any[], id: string): any {
    for (const item of items) {
      if (item.element && item.element.id === id) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = this.findElementById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Recursively finds all sample title elements in the DOM structure
   * @param items - Array of DOM elements to search through
   * @returns Array of sample title elements sorted by vertical position
   */
  private findAllSampleTitles(items: any[]): any[] {
    const sampleTitles: any[] = [];
    for (const item of items) {
      if (item.element && item.element.classList && item.element.classList.contains('sample-title')) {
        sampleTitles.push(item);
      }
      if (item.children && item.children.length > 0) {
        sampleTitles.push(...this.findAllSampleTitles(item.children));
      }
    }
    return sampleTitles.sort((a, b) => a.yPosition - b.yPosition);
  }

  /**
   * Analyzes the DOM structure and extracts element positioning information
   *
   * This method performs a comprehensive analysis of the HTML container to extract
   * positioning data for all elements. It's essential for the intelligent page break
   * calculations used in PDF generation.
   *
   * @param container - The HTML container element to analyze
   * @returns Array of element objects with position and dimension information
   */
  public getHtmlElementsInfo(container: HTMLElement) {
    const processElement = (element: HTMLElement, initialOffset: number, inheritedOffset = 0): any => {
      const rawOffsetTop = inheritedOffset;

      const height = this.calculateElementHeight(element, this.SCALE);
      let children = [];
      if (this.includeChildren(element)) {
        // Check for redundant wrappers
        if (element.children.length === 1) {
          const child = element.children[0] as HTMLElement;
          if (this.includeChildren(child)) {
            const childHeight = this.calculateElementHeight(child, this.SCALE);
            const childOffset = child.offsetTop * this.SCALE;

            if (childHeight === height && childOffset === rawOffsetTop) {
              return processElement(child, initialOffset, rawOffsetTop);
            }
          }
        }

        let cumulativeOffset = inheritedOffset;

        children = Array.from(element.children).map((child) => {
          const childElement = child as HTMLElement;

          const childInfo = processElement(childElement, initialOffset, cumulativeOffset);

          // Update cumulative offset for the next child
          cumulativeOffset += childInfo.height;

          return childInfo;
        });
      }
      return {
        id: element.id,
        element,
        yPosition: rawOffsetTop === 0 ? initialOffset : rawOffsetTop,
        height,
        children,
      };
    };

    const initialOffset = 0;

    // Skip redundant wrappers
    let topLevelChildren = Array.from(container.children);
    if (topLevelChildren.length === 1) {
      let singleChild = topLevelChildren[0] as HTMLElement;
      while (
        singleChild.children.length === 1 &&
        this.calculateElementHeight(singleChild, this.SCALE) ===
          this.calculateElementHeight(singleChild.children[0] as HTMLElement, this.SCALE) &&
        singleChild.offsetTop === container.offsetTop
      ) {
        singleChild = singleChild.children[0] as HTMLElement;
      }
      topLevelChildren = Array.from(singleChild.children);
    }

    return topLevelChildren.reduce((acc, child, i) => {
      let inheritedOffset = 0;

      if (i > 0) {
        inheritedOffset = acc[i - 1].yPosition + acc[i - 1].height;
      }

      acc.push(processElement(child as HTMLElement, initialOffset, inheritedOffset));
      return acc;
    }, []);
  }

  /**
   * Calculates the height of an HTML element with proper scaling
   *
   * This method determines the actual rendered height of an element,
   * accounting for the PDF scale factor. It handles different element
   * types and uses the most accurate measurement available.
   *
   * @param element - The HTML element to measure
   * @param scale - The scale factor applied to the element
   * @returns The calculated height in pixels
   */
  private calculateElementHeight(element: HTMLElement, scale: number): number {
    if (element.clientHeight > 0) {
      return element.getBoundingClientRect().height * scale;
    }

    return Array.from(element.children).reduce((total, child) => {
      const childElement = child as HTMLElement;
      return total + childElement.offsetHeight * scale;
    }, 0);
  }

  /**
   * Determines whether an element should have its children processed
   *
   * This method filters out elements that don't need child processing
   * during DOM analysis. It helps optimize the analysis by skipping
   * elements like formatting tags that don't contribute to layout.
   *
   * @param element - The HTML element to check
   * @returns True if children should be processed, false otherwise
   */
  private includeChildren(element: HTMLElement) {
    return (
      element.tagName !== 'I' &&
      element.tagName !== 'B' &&
      !element.tagName.startsWith('H') &&
      !element.id.includes('label-row')
    );
  }
}
